import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Avatar,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyRole, setCompanyRole] = useState('');
  const [orgName, setOrgName] = useState('');
  const [numEmployees, setNumEmployees] = useState('');
  const [hearAboutUs, setHearAboutUs] = useState('');
  const [hearAboutUsOther, setHearAboutUsOther] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    if (!orgName || !numEmployees || !hearAboutUs) {
      setError('Please fill in all organization fields.');
      return;
    }

    if (hearAboutUs === 'Other' && !hearAboutUsOther) {
      setError('Please specify how you heard about us.');
      return;
    }

    setLoading(true);

    try {
      const hearAboutValue = hearAboutUs === 'Other' ? hearAboutUsOther : hearAboutUs;

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          phone,
          company_role: companyRole,
          org_name: orgName,
          num_employees: numEmployees,
          hear_about_us: hearAboutValue,
        }),
      });

      const data = await response.json();
      setLoading(false);

      if (!response.ok) {
        setError(data.error || 'Unable to submit registration request.');
        return;
      }

      setSuccess('Your registration request has been submitted and is awaiting admin approval.');
    } catch (err) {
      setLoading(false);
      setError('Unable to connect to server.');
    }
  };

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh' }}>
      <Grid item xs={11} sm={8} md={4}>
        <Paper elevation={6} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>

            <Typography component="h1" variant="h5">
              Request Access
            </Typography>

            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              New accounts require admin approval before login is enabled.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <TextField
                margin="normal"
                fullWidth
                label="Phone Number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="company-role-label">Company Role</InputLabel>
                <Select
                  labelId="company-role-label"
                  value={companyRole}
                  label="Company Role"
                  onChange={(e) => setCompanyRole(e.target.value)}
                >
                  <MenuItem value="CEO">CEO</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="Employee">Employee</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                margin="normal"
                required
                fullWidth
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="num-employees-label">Number of Employees</InputLabel>
                <Select
                  labelId="num-employees-label"
                  value={numEmployees}
                  label="Number of Employees"
                  onChange={(e) => setNumEmployees(e.target.value)}
                >
                  <MenuItem value="1-10">1-10</MenuItem>
                  <MenuItem value="11-50">11-50</MenuItem>
                  <MenuItem value="51-200">51-200</MenuItem>
                  <MenuItem value="201-1000">201-1000</MenuItem>
                  <MenuItem value="1000+">1000+</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="hear-about-us-label">How did you hear about us?</InputLabel>
                <Select
                  labelId="hear-about-us-label"
                  value={hearAboutUs}
                  label="How did you hear about us?"
                  onChange={(e) => {
                    setHearAboutUs(e.target.value);
                    if (e.target.value !== 'Other') setHearAboutUsOther('');
                  }}
                >
                  <MenuItem value="Search Engine">Search Engine</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              {hearAboutUs === 'Other' && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Please specify"
                  value={hearAboutUsOther}
                  onChange={(e) => setHearAboutUsOther(e.target.value)}
                />
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>

              <Button
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={() => navigate('/login')}
              >
                Already have an account? Log in
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}